import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TicketForm from './TicketForm';
import FilterDropdown from './FilterDropdown';
import UniformTable from './UniformTable';
import './Support.css';

const API_BASE = process.env.REACT_APP_SUPPORT_API_BASE;

export default function Support({ user }) {
  const [view, setView] = useState('list');
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const userId = user || localStorage.getItem('authUser') || '';

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!userId) {
        throw new Error('Unable to detect signed-in user. Please login again.');
      }
      let url = `${API_BASE}/tickets?userId=${encodeURIComponent(userId)}`;
      if (statusFilter !== 'All') {
        url += `&status=${encodeURIComponent(statusFilter)}`;
      }
      const response = await fetch(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Unable to load tickets (${response.status})`);
      }
      const data = await response.json();
      setTickets(Array.isArray(data.tickets) ? data.tickets : Array.isArray(data.data?.tickets) ? data.data.tickets : Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[Support] Fetch tickets failed', err);
      setError(err.message || 'Failed to load support tickets.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, userId]);

  useEffect(() => {
    const handlePopState = () => {
      setView(window.location.pathname === '/support/new' ? 'form' : 'list');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets, refreshTrigger]);

  const handleRefresh = () => setRefreshTrigger((count) => count + 1);

  const handleTicketCreated = () => {
    toast.success('Ticket submitted successfully.');
    setView('list');
    handleRefresh();
  };

  return (
    <div className="support-root">
      <div className="support-topbar">


        <div className="support-actions">
          {view === 'list' && (
            <>
              <FilterDropdown
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'All', label: 'All Status' },
                  { value: 'Pending', label: 'Pending' },
                  { value: 'Resolved', label: 'Resolved' }
                ]}
              />
              <button className="support-button primary" onClick={() => {
                setView('form');
                window.history.pushState({}, '', '/support/new');
              }}>+ Raise Ticket</button>
            </>
          )}
        </div>
      </div>

      {view === 'form' ? (
        <>
          <div className="ticket-form-header">
            <button
              className="support-button secondary"
              onClick={() => window.history.back()}
            >
              Back to Tickets
            </button>
          </div>

          <TicketForm
            user={userId}
            onCancel={() => setView('list')}
            onSuccess={handleTicketCreated}
          />
        </>
      ) : (
        <div className="support-card">
          <div className="support-card-header">
            <div>
              <h3>Your Tickets</h3>
            </div>
            <button className="support-button tertiary" onClick={handleRefresh}>
              Refresh
            </button>
          </div>

          <div className="support-table-wrapper">
            {loading ? (
              <div className="support-empty-state">
                <div className="support-spinner" />
                Loading tickets...
              </div>
            ) : error ? (
              <div className="support-empty-state support-error">
                <strong>Error:</strong> {error}
              </div>
            ) : tickets.length > 0 ? (
              <>
                <div className="support-table-wrapper--desktop">
                  <UniformTable
                    columns={[
                      { header: 'Ticket ID', accessor: 'ticketId', width: '1.3fr', align: 'left' },
                      { header: 'Vehicle', accessor: 'vehicle', width: '1.2fr', align: 'left' },
                      { header: 'Status', accessor: 'status', width: '1fr', align: 'left' },
                      { header: 'Category', accessor: 'category', width: '1.2fr', align: 'left' },
                      { header: 'Subcategory', accessor: 'subcategory', width: '1.2fr', align: 'left' },
                      { header: 'Created', accessor: 'created', width: '1.3fr', align: 'left' },
                      { header: 'Updated', accessor: 'updated', width: '1.3fr', align: 'left' }
                    ]}
                    data={tickets.map((ticket) => {
                      const statusText = ticket.status || ticket.ticket_status || 'Pending';
                      return {
                        ticketId: ticket.ticketid || ticket.ticketId || 'N/A',
                        vehicle: ticket.vehicleId || ticket.vehicle_id || 'N/A',
                        status: (
                          <span className={`status-badge status-${String(statusText || 'pending').toLowerCase().replace(/\s+/g, '-')}`}>
                            {statusText}
                          </span>
                        ),
                        category: ticket.category || 'N/A',
                        subcategory: ticket.subcategory || ticket.sub_category || 'N/A',
                        created: ticket.created_at || ticket.createdAt || 'N/A',
                        updated: ticket.updated_at || ticket.updatedAt || 'N/A'
                      };
                    })}
                  />
                </div>

                <div className="support-mobile-list">
                  {tickets.map((ticket, index) => {
                    const statusText = ticket.status || ticket.ticket_status || 'Pending';
                    return (
                      <div key={ticket.ticketid || ticket.ticketId || index} className="support-mobile-card">
                        <div className="mobile-device-card-header">
                          <h3 className="mobile-device-name">{ticket.ticketid || ticket.ticketId || 'N/A'}</h3>
                          <span className={`status-badge status-${String(statusText || 'pending').toLowerCase().replace(/\s+/g, '-')}`}>
                            {statusText}
                          </span>
                        </div>
                        <div className="mobile-device-card-body">
                          <div className="mobile-device-info-row">
                            <span className="mobile-info-label">Vehicle</span>
                            <span className="mobile-info-value">{ticket.vehicleId || ticket.vehicle_id || 'N/A'}</span>
                          </div>
                          <div className="mobile-device-info-row">
                            <span className="mobile-info-label">Category</span>
                            <span className="mobile-info-value">{ticket.category || 'N/A'}</span>
                          </div>
                          <div className="mobile-device-info-row">
                            <span className="mobile-info-label">Subcategory</span>
                            <span className="mobile-info-value">{ticket.subcategory || ticket.sub_category || 'N/A'}</span>
                          </div>
                          <div className="mobile-device-info-row">
                            <span className="mobile-info-label">Created</span>
                            <span className="mobile-info-value">{ticket.created_at || ticket.createdAt || 'N/A'}</span>
                          </div>
                          <div className="mobile-device-info-row">
                            <span className="mobile-info-label">Updated</span>
                            <span className="mobile-info-value">{ticket.updated_at || ticket.updatedAt || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="support-empty-state">
                <p>No tickets yet.</p>
                <p>Click "Raise Ticket" to create a new support request.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
